import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Shield, 
  Settings, 
  AlertCircle, 
  CheckCircle, 
  Info,
  Cookie,
  BarChart3,
  Mail,
  Users,
  Brain,
  FileText,
  Database,
  Eye
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { gdprService } from '@/services/gdpr';

interface ConsentPurpose {
  id: string;
  title: string;
  description: string;
  lawfulBasis: string;
  category: 'essential' | 'analytics' | 'marketing' | 'functional' | 'personalization';
  required: boolean;
  icon: React.ElementType;
  dataTypes: string[];
  retentionPeriod: string;
  thirdParties?: string[];
}

interface UserConsent {
  purpose_id: string;
  granted: boolean;
  granted_at: string;
  withdrawn_at?: string;
  consent_version: string;
  ip_address?: string;
  user_agent?: string;
}

const consentPurposes: ConsentPurpose[] = [
  {
    id: 'essential-operations',
    title: 'Essential Operations',
    description: 'Core functionality required for the platform to work, including user authentication, security, and basic service provision.',
    lawfulBasis: 'Legitimate Interest & Contract Performance',
    category: 'essential',
    required: true,
    icon: Shield,
    dataTypes: ['Authentication data', 'Security logs', 'Account information', 'Usage statistics'],
    retentionPeriod: '7 years (legal requirement)',
    thirdParties: ['Supabase (hosting)', 'Cloudflare (security)']
  },
  {
    id: 'ai-analysis',
    title: 'AI-Powered Analysis',
    description: 'Processing candidate resumes and job descriptions using AI to provide matching, scoring, and insights.',
    lawfulBasis: 'Legitimate Interest & Contract Performance',
    category: 'functional',
    required: true,
    icon: Brain,
    dataTypes: ['Resume content', 'Job descriptions', 'Analysis results', 'Matching scores'],
    retentionPeriod: '3 years (business requirement)',
    thirdParties: ['OpenAI (AI processing)', 'Anthropic (AI processing)']
  },
  {
    id: 'performance-analytics',
    title: 'Performance Analytics',
    description: 'Analyzing how you use our platform to improve performance, fix bugs, and enhance user experience.',
    lawfulBasis: 'Legitimate Interest',
    category: 'analytics',
    required: false,
    icon: BarChart3,
    dataTypes: ['Page views', 'Click patterns', 'Feature usage', 'Error logs'],
    retentionPeriod: '2 years',
    thirdParties: ['Google Analytics', 'Sentry (error tracking)']
  },
  {
    id: 'marketing-communications',
    title: 'Marketing Communications',
    description: 'Sending you product updates, newsletters, and promotional materials about our services.',
    lawfulBasis: 'Consent',
    category: 'marketing',
    required: false,
    icon: Mail,
    dataTypes: ['Email address', 'Communication preferences', 'Engagement history'],
    retentionPeriod: '3 years or until withdrawal',
    thirdParties: ['Mailchimp (email service)', 'Stripe (billing communications)']
  },
  {
    id: 'personalization',
    title: 'Personalization',
    description: 'Customizing your experience with personalized recommendations, saved preferences, and tailored content.',
    lawfulBasis: 'Legitimate Interest',
    category: 'personalization',
    required: false,
    icon: Users,
    dataTypes: ['User preferences', 'Interaction history', 'Recommendation data'],
    retentionPeriod: '2 years',
    thirdParties: []
  },
  {
    id: 'market-research',
    title: 'Market Research',
    description: 'Using aggregated, anonymized data to understand market trends and improve our services.',
    lawfulBasis: 'Legitimate Interest',
    category: 'analytics',
    required: false,
    icon: FileText,
    dataTypes: ['Anonymized usage patterns', 'Industry insights', 'Feature adoption rates'],
    retentionPeriod: '5 years (anonymized)',
    thirdParties: []
  },
  {
    id: 'cookies-tracking',
    title: 'Cookies & Tracking',
    description: 'Using cookies and similar technologies for analytics, personalization, and advertising.',
    lawfulBasis: 'Consent',
    category: 'functional',
    required: false,
    icon: Cookie,
    dataTypes: ['Cookie data', 'Browser fingerprints', 'Session tokens'],
    retentionPeriod: '1 year',
    thirdParties: ['Google Analytics', 'Cloudflare']
  },
  {
    id: 'data-sharing',
    title: 'Data Sharing with Partners',
    description: 'Sharing anonymized insights with trusted partners for industry research and benchmarking.',
    lawfulBasis: 'Legitimate Interest',
    category: 'analytics',
    required: false,
    icon: Database,
    dataTypes: ['Anonymized metrics', 'Industry benchmarks', 'Usage patterns'],
    retentionPeriod: '3 years (anonymized)',
    thirdParties: ['Industry research partners', 'Academic institutions']
  }
];

export function ConsentManagement() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [userConsents, setUserConsents] = useState<UserConsent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedPurpose, setExpandedPurpose] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadUserConsents();
    }
  }, [user]);

  const loadUserConsents = async () => {
    if (!user) return;
    
    try {
      const consents = await gdprService.getUserConsentsForComponent(user.id);
      setUserConsents(consents);
    } catch (error) {
      console.error('Error loading user consents:', error);
      toast({
        title: "Error Loading Consents",
        description: "Failed to load your consent preferences. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConsentChange = async (purposeId: string, granted: boolean) => {
    if (!user) return;
    
    setSaving(true);
    
    try {
      await gdprService.updateUserConsent(user.id, purposeId, granted);
      
      // Update local state
      setUserConsents(prev => {
        const existing = prev.find(c => c.purpose_id === purposeId);
        if (existing) {
          return prev.map(c => 
            c.purpose_id === purposeId 
              ? { ...c, granted, granted_at: new Date().toISOString(), withdrawn_at: granted ? undefined : new Date().toISOString() }
              : c
          );
        } else {
          return [...prev, {
            purpose_id: purposeId,
            granted,
            granted_at: new Date().toISOString(),
            consent_version: '1.0',
            ip_address: undefined,
            user_agent: navigator.userAgent
          }];
        }
      });

      toast({
        title: granted ? "Consent Granted" : "Consent Withdrawn",
        description: `Your consent for ${consentPurposes.find(p => p.id === purposeId)?.title} has been ${granted ? 'granted' : 'withdrawn'}.`,
        variant: "default"
      });
    } catch (error) {
      console.error('Error updating consent:', error);
      toast({
        title: "Error Updating Consent",
        description: "Failed to update your consent preference. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const getConsentStatus = (purposeId: string): boolean => {
    const consent = userConsents.find(c => c.purpose_id === purposeId);
    return consent?.granted || false;
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'essential': return 'bg-red-50 text-red-700 border-red-200';
      case 'functional': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'analytics': return 'bg-green-50 text-green-700 border-green-200';
      case 'marketing': return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'personalization': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'essential': return Shield;
      case 'functional': return Settings;
      case 'analytics': return BarChart3;
      case 'marketing': return Mail;
      case 'personalization': return Users;
      default: return Info;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading consent preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <Badge variant="outline" className="mb-4">
            <Shield className="h-4 w-4 mr-2" />
            GDPR Consent Management
          </Badge>
          <h1 className="text-3xl font-bold">Privacy Preferences</h1>
          <p className="text-muted-foreground max-w-3xl mx-auto">
            Control how your data is used. You can change these preferences at any time. 
            Essential services cannot be disabled as they are required for the platform to function.
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {userConsents.filter(c => c.granted).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Active Consents</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {consentPurposes.length}
                  </div>
                  <div className="text-sm text-muted-foreground">Total Purposes</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Settings className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">
                    {consentPurposes.filter(p => !p.required).length}
                  </div>
                  <div className="text-sm text-muted-foreground">Optional</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Consent Purposes */}
        <div className="space-y-4">
          {consentPurposes.map((purpose) => {
            const Icon = purpose.icon;
            const CategoryIcon = getCategoryIcon(purpose.category);
            const isGranted = getConsentStatus(purpose.id);
            const isExpanded = expandedPurpose === purpose.id;

            return (
              <Card key={purpose.id} className="border-2 transition-all duration-200 hover:shadow-md">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`h-12 w-12 rounded-lg flex items-center justify-center ${
                        purpose.required 
                          ? 'bg-red-100' 
                          : isGranted 
                            ? 'bg-green-100' 
                            : 'bg-gray-100'
                      }`}>
                        <Icon className={`h-6 w-6 ${
                          purpose.required 
                            ? 'text-red-600' 
                            : isGranted 
                              ? 'text-green-600' 
                              : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <CardTitle className="text-lg">{purpose.title}</CardTitle>
                          <Badge variant="outline" className={getCategoryColor(purpose.category)}>
                            <CategoryIcon className="h-3 w-3 mr-1" />
                            {purpose.category}
                          </Badge>
                          {purpose.required && (
                            <Badge variant="destructive" className="text-xs">
                              Required
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{purpose.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={isGranted}
                        onCheckedChange={(checked) => handleConsentChange(purpose.id, checked)}
                        disabled={purpose.required || saving}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExpandedPurpose(isExpanded ? null : purpose.id)}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        {isExpanded ? 'Less' : 'More'} Info
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="pt-0">
                    <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Lawful Basis</h4>
                          <p className="text-sm text-muted-foreground">{purpose.lawfulBasis}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Retention Period</h4>
                          <p className="text-sm text-muted-foreground">{purpose.retentionPeriod}</p>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Data Types Processed</h4>
                        <div className="flex flex-wrap gap-2">
                          {purpose.dataTypes.map(dataType => (
                            <Badge key={dataType} variant="secondary" className="text-xs">
                              {dataType}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      {purpose.thirdParties && purpose.thirdParties.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-sm mb-2">Third Parties</h4>
                          <div className="flex flex-wrap gap-2">
                            {purpose.thirdParties.map(party => (
                              <Badge key={party} variant="outline" className="text-xs">
                                {party}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Information Box */}
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Your Rights:</strong> You can withdraw your consent at any time. 
            This will not affect the lawfulness of processing based on consent before its withdrawal. 
            For essential services, we rely on legitimate interests or contractual necessity as our lawful basis.
          </AlertDescription>
        </Alert>

        {/* Actions */}
        <div className="flex justify-center gap-4 pt-6">
          <Button
            variant="outline"
            onClick={() => window.location.href = '/privacy-policy'}
            className="flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Read Privacy Policy
          </Button>
          <Button
            variant="outline"
            onClick={() => window.location.href = '/data-rights'}
            className="flex items-center gap-2"
          >
            <Shield className="h-4 w-4" />
            Data Rights Center
          </Button>
        </div>
      </div>
    </div>
  );
} 